import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError, getActiveChild, setActiveChild } from './lib/api'
import { ToastProvider, useToast } from './lib/toast'
import { mondayOf } from './lib/dates'
import { initialTabFromUrl, initialWeekFromUrl, usePwa, type Pwa } from './lib/pwa'
import { useTheme, type Theme } from './lib/theme'
import { SignIn } from './components/SignIn'
import { SignUp } from './components/SignUp'
import { JoinPage } from './components/JoinPage'
import { Onboarding } from './components/Onboarding'
import { AdminPage } from './components/AdminPage'
import { AllergyCard } from './components/AllergyCard'
import { TabBar } from './components/TabBar'
import { Menu } from './components/Menu'
import { Modal } from './components/Modal'
import { PHONE, useMediaQuery } from './lib/media'
import { markSignedIn } from './lib/appCookie'
import { canNudge, silenceNudge } from './lib/install'
import { InstallPrompt } from './components/InstallPrompt'
import { FeedbackTab } from './components/FeedbackTab'
import { FeedbackModal } from './components/FeedbackModal'
import { ResetPassword } from './components/ResetPassword'
import { authClient } from './lib/authClient'
import { PlanView, dayFill } from './components/PlanView'
import { PantryPage } from './components/PantryPage'
import { MealsPage } from './components/MealsPage'
import { AssistantPage } from './components/AssistantPage'
import { SettingsPage } from './components/SettingsPage'
import { HistoryPage } from './components/HistoryPage'
import { ShoppingPage } from './components/ShoppingPage'
import type {
  Child,
  Bootstrap,
  Item,
  Meal,
  Person,
  ProfileAllergen,
  ReminderSettings,
  Session,
  Week,
  WeekSummary,
} from '@shared/types'

type Tab = 'plan' | 'pantry' | 'meals' | 'assistant' | 'history' | 'shop' | 'admin' | 'settings'

const TABS: { id: Tab; label: string }[] = [
  { id: 'plan', label: 'Plan' },
  { id: 'pantry', label: 'Pantry' },
  { id: 'meals', label: 'Meals' },
  { id: 'assistant', label: '✨ Ask' },
  { id: 'history', label: 'History' },
  { id: 'shop', label: 'Shopping' },
]
/** Only for ADMIN_EMAILS accounts; the server 404s it for everyone else. */
const ADMIN_TAB: { id: Tab; label: string } = { id: 'admin', label: 'Admin' }

/** The phone's bottom bar: four every-week places; the rest live under More. */
const BAR_TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'plan', label: 'Plan', icon: '🗓' },
  { id: 'pantry', label: 'Pantry', icon: '🧺' },
  { id: 'meals', label: 'Meals', icon: '🍱' },
  { id: 'shop', label: 'Shop', icon: '🛒' },
]

/** How often to pick up the other parent's edits while the tab is visible. */
const POLL_MS = 20_000

/**
 * Replace state only when the payload actually differs.
 *
 * The poll used to hand React a brand-new object every 20 seconds even when
 * nothing had changed, which re-rendered the whole tree -- and worse, gave
 * `weeks.find(...)` a new identity, re-firing the week-loading effect and
 * blanking the board to "Loading…" on every tick. Visible as a flash on a
 * tablet left open. These payloads are a few KB, so comparing them is far
 * cheaper than the render they avoid.
 */
function keepIfSame<T>(next: T): (prev: T | null) => T {
  return (prev) => (prev !== null && JSON.stringify(prev) === JSON.stringify(next) ? prev : next)
}

export default function App() {
  return (
    <ToastProvider>
      <Root />
    </ToastProvider>
  )
}

function Root() {
  // Registered here rather than inside Shell: the app shell should be cached
  // from the first visit, not the first successful sign-in.
  const pwa = usePwa()
  // Above the lock screen too, so signing in is not the first time the app
  // agrees with the theme you chose.
  const theme = useTheme()
  const [session, setSession] = useState<Session | null>(null)
  // Tell the marketing site at the apex whether this browser is signed in,
  // so it can skip the pitch and open the app straight away.
  useEffect(() => {
    markSignedIn(session !== null)
  }, [session])
  const [checking, setChecking] = useState(true)

  /*
   * Signed out, and a new build is waiting: take it now. There is nothing on
   * a sign-in screen worth preserving, and the alternative is the one that
   * happened -- a cached shell from before an auth change, posting to a route
   * that no longer exists, with the "new version" strip only ever shown
   * inside the signed-in app.
   */
  const { updateReady, applyUpdate, applyIfIdle } = pwa
  useEffect(() => {
    if (updateReady && !checking && !session) applyUpdate()
  }, [updateReady, checking, session, applyUpdate])

  /*
   * Signed in, and a new build is waiting: take it when returning to the app
   * after a real absence, so nobody has to notice the strip at all.
   *
   * Two things have to be true first. The hook checks the absence was long
   * enough to count as coming back rather than glancing away. This checks the
   * screen can afford to be pulled out from under them: a dialog open means a
   * half-written note, a picker mid-choice, or a warning being read, and none
   * of those survive a reload. The strip stays for anyone in that state, and
   * the next return picks it up.
   */
  useEffect(() => {
    if (!updateReady || checking || !session) return
    /*
     * Not during onboarding. It renders on a session that has no family yet,
     * and every answer in it -- the names, the allergens, the restrictions,
     * ninety pantry ticks -- lives in React state until the last step writes
     * them. A reload there is not an interruption, it is starting over, and
     * it would land on the one person who has least reason to forgive it.
     */
    if (!session.familyId) return
    if (document.documentElement.dataset.modals) return
    applyIfIdle()
  }, [updateReady, checking, session, applyIfIdle])

  useEffect(() => {
    let cancelled = false
    api
      .me()
      .then((r) => {
        if (!cancelled) setSession(r.session)
      })
      .catch(() => {
        /* 401 -- the lock screen is the right answer */
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (checking) {
    return (
      <div className="lock">
        <p className="muted">Loading…</p>
      </div>
    )
  }
  // A few paths are pages of their own, reached from an email rather than a
  // tab: the reset link, sign-up, and a partner's invitation. Everything else
  // is the app, or the sign-in screen in front of it.
  const path = window.location.pathname
  if (path === '/reset-password') return <ResetPassword />
  if (path === '/signup' && !session) return <SignUp />
  if (path === '/join') return <JoinPage session={session} onJoined={setSession} />
  if (!session) return <SignIn onSignedIn={setSession} />
  // Signed in, no family: the confirmation link lands here (/welcome), and so
  // does anyone whose account outlived their family.
  /*
   * ?preview=onboarding walks an admin through what a new family sees,
   * writing nothing. Admins only: it is a look at the product, but it is
   * still a screen that talks as if it owns your account.
   */
  const previewing = new URLSearchParams(window.location.search).get('preview') === 'onboarding'
  if (previewing && session.isAdmin) {
    return (
      <Onboarding
        session={session}
        pwa={pwa}
        preview
        onDone={setSession}
        onSignedOut={() => setSession(null)}
      />
    )
  }
  if (!session.familyId) {
    return <Onboarding session={session} pwa={pwa} onDone={setSession} onSignedOut={() => setSession(null)} />
  }
  // Signed in with a family, on a path that only means something without one.
  if (path === '/welcome' || path === '/signup') window.history.replaceState(null, '', '/')
  /*
   * The school card is a page rather than a tab: it is printed, or opened in
   * a browser tab and held up at a classroom door, and it has to survive
   * Cmd-P with none of the app around it.
   */
  if (path === '/allergy-card') return <AllergyCard />
  return (
    <Shell
      session={session}
      pwa={pwa}
      theme={theme}
      onSignedOut={() => setSession(null)}
      onSessionChanged={setSession}
    />
  )
}

function Shell({
  session,
  pwa,
  theme,
  onSignedOut,
  onSessionChanged,
}: {
  session: Session
  pwa: Pwa
  theme: Theme
  onSignedOut: () => void
  onSessionChanged: (session: Session) => void
}) {
  const toast = useToast()
  const phone = useMediaQuery(PHONE)
  const [declined, setDeclined] = useState<string | null>(null)
  const [boot, setBoot] = useState<Bootstrap | null>(null)
  const [children, setChildren] = useState<Child[]>([])

  /*
   * The assistant is hidden rather than shown switched off. A tab that
   * answers "not for you" every time it is tapped is worse than no tab, and
   * the pending banner already says the assistant exists and what turns it
   * on. It also stays hidden when the Worker holds no OpenAI key at all.
   */
  const tabs = useMemo(() => {
    const base = TABS.filter((t) => t.id !== 'assistant' || boot?.aiAvailable)
    return session.isAdmin ? [...base, ADMIN_TAB] : base
  }, [session.isAdmin, boot?.aiAvailable])

  // Manifest shortcuts deep-link with ?tab=shop, so honour that on launch.
  const [tab, setTab] = useState<Tab>(
    () => (initialTabFromUrl([...TABS.map((t) => t.id), 'settings']) as Tab | null) ?? 'plan',
  )

  /*
   * A tab can stop existing under you: ?tab=assistant on a pending family, or
   * approval being withdrawn while the tab is open. Fall back rather than
   * render a page the family cannot use.
   */
  useEffect(() => {
    // Settings is reached from the cog rather than the nav, so it is never in
    // `tabs` and must not be mistaken for one that has gone away.
    if (boot && tab !== 'settings' && !tabs.some((t) => t.id === tab)) setTab('plan')
  }, [boot, tabs, tab])
  const [weeks, setWeeks] = useState<WeekSummary[]>([])
  // A notification links straight at the week it is about (?week=YYYY-MM-DD),
  // which is the only reason worth sending one.
  const [weekStart, setWeekStart] = useState(() => initialWeekFromUrl() ?? mondayOf())
  /** The one-field "add another child" from the switcher. */
  const [addingChild, setAddingChild] = useState(false)
  /*
   * Settings can hold a half-finished allergen edit. The tab lives here, so
   * this is the only place that can catch a tap on Plan before the edit goes
   * with it. `leaving` holds the tab being asked for while the question is up.
   */
  const [settingsDirty, setSettingsDirty] = useState(false)
  /* Select mode in the pantry, which takes the corner the feedback button sits in. */
  const [pantryPicking, setPantryPicking] = useState(false)
  /** The move being asked about: another tab, or another child. */
  const [leaving, setLeaving] = useState<{ tab: Tab } | { child: string } | null>(null)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  /*
   * Whether this device is still worth offering the home-screen install to.
   * One flag for both places that ask, so a "not now" anywhere silences it
   * everywhere and nobody is asked the same question twice.
   */
  const [nudge, setNudge] = useState(() => canNudge())
  const [installOpen, setInstallOpen] = useState<'planned' | 'plain' | null>(null)
  const asked = useRef(false)
  const seenWeek = useRef<{ id: string; complete: boolean } | null>(null)

  const dismissNudge = useCallback(() => {
    silenceNudge()
    setNudge(false)
  }, [])

  const [week, setWeek] = useState<Week | null>(null)
  const [loadingWeek, setLoadingWeek] = useState(true)
  const [fatal, setFatal] = useState<string | null>(null)

  /*
   * Offer it at the moment a week becomes planned, which is when the next
   * thing someone wants is this in the kitchen or the shop. Anything earlier
   * interrupts the thing they came to do.
   *
   * The transition is the trigger, not the state. Opening the app onto a week
   * you finished yesterday should not be greeted by a modal, and walking
   * forward into a week that happens to be full is not an achievement. So it
   * fires only when this same week goes from having a gap to having none. A
   * skipped day needs nothing and a pizza day needs no main, which is what
   * dayFill already knows.
   */
  useEffect(() => {
    if (asked.current || !nudge || !week || !boot) return
    const days = week.lunches.filter((l) => l.status !== 'skip')
    const complete = days.length > 0 && days.every((l) => dayFill(l, boot.categories).empty.length === 0)
    const before = seenWeek.current
    seenWeek.current = { id: week.id, complete }
    if (before && before.id === week.id && !before.complete && complete) {
      asked.current = true
      setInstallOpen('planned')
    }
  }, [week, boot, nudge])

  /** Surface an API failure without losing the user's place. */
  const report = useCallback(
    (err: unknown, fallback: string) => {
      const message = err instanceof ApiError ? err.message : fallback
      if (err instanceof ApiError && err.status === 401) {
        onSignedOut()
        return
      }
      toast.warn(message)
    },
    [toast, onSignedOut],
  )

  const refreshBoot = useCallback(async () => {
    try {
      const fresh = await api.bootstrap()
      setBoot(keepIfSame(fresh))
      // Approval happens on the server without a sign-in: the poll is how the
      // pending banner goes away and the assistant switches on.
      if (fresh.session.familyStatus !== session.familyStatus) onSessionChanged(fresh.session)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onSignedOut()
      else if (err instanceof ApiError && err.code === 'needs_family') {
        onSessionChanged({ ...session, familyId: null, familyName: null, familyStatus: null })
      } else if (err instanceof ApiError && err.code === 'family_declined') setDeclined(err.message)
      else setFatal('Could not load your pantry. Check your connection and reload.')
    }
  }, [onSignedOut, onSessionChanged, session])

  const refreshWeeks = useCallback(async () => {
    try {
      const fresh = await api.listWeeks()
      setWeeks(keepIfSame(fresh))
    } catch (err) {
      report(err, 'Could not load your weeks.')
    }
  }, [report])

  const refreshChildren = useCallback(async () => {
    try {
      setChildren(await api.children())
    } catch {
      // Not worth a toast: with no list the header simply shows one name,
      // which is what it showed before there was a list at all.
    }
  }, [])

  useEffect(() => {
    void refreshBoot()
    void refreshWeeks()
    void refreshChildren()
  }, [refreshBoot, refreshWeeks, refreshChildren])

  /**
   * Show a different child.
   *
   * The week being viewed stays the same Monday -- switching child is not
   * switching week -- and the board re-derives from the new child's own
   * summaries, so a sibling with nothing planned that week lands on the
   * starter prompt rather than the other one's lunches.
   */
  const switchChild = useCallback(
    async (id: string) => {
      if (id === getActiveChild()) return
      setActiveChild(id)
      await Promise.all([refreshBoot(), refreshWeeks()])
    },
    [refreshBoot, refreshWeeks],
  )

  /*
   * Asked before switching child too, not just before changing tab. The
   * switcher sits in the header and is reachable from Settings, and the edit
   * it would discard belongs to the child being left.
   */
  const askThenSwitchChild = useCallback(
    (id: string) => {
      if (tab === 'settings' && settingsDirty && id !== getActiveChild()) {
        setLeaving({ child: id })
        return
      }
      void switchChild(id)
    },
    [tab, settingsDirty, switchChild],
  )

  /**
   * Change tab, asking first if that would throw away an edit.
   *
   * Only leaving Settings altogether loses anything: the allergen draft is
   * shared across its sections, so moving between them keeps it.
   */
  const changeTab = useCallback(
    (next: Tab) => {
      if (tab === 'settings' && next !== 'settings' && settingsDirty) {
        setLeaving({ tab: next })
        return
      }
      setTab(next)
    },
    [tab, settingsDirty],
  )

  /*
   * And the browser's own way out. This one cannot be styled or worded -- the
   * browser decides -- but closing the tab on an unsaved allergy is worth its
   * generic dialog.
   */
  useEffect(() => {
    if (!settingsDirty) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [settingsDirty])

  // Shell only mounts once signed in, which is exactly when the session check
  // is worth caching for an offline launch.
  const { cacheSession } = pwa
  useEffect(() => {
    cacheSession()
  }, [cacheSession])

  /* Load whichever week is being viewed. A week that has never been planned
     simply has no row yet -- we show a starter prompt rather than silently
     creating empty rows as you page through the calendar. */
  const summary = useMemo(
    () => weeks.find((w) => w.startDate === weekStart) ?? null,
    [weeks, weekStart],
  )

  const summaryId = summary?.id ?? null

  /*
   * Keyed on the week's id, not the summary object: a poll that finds nothing
   * changed must not re-run this. And the full-screen loader only appears when
   * there is genuinely nothing to show -- swapping a loaded board for "Loading…"
   * is what the refresh looked like from the outside.
   */
  useEffect(() => {
    let cancelled = false
    if (!summaryId) {
      setWeek(null)
      setLoadingWeek(false)
      return
    }
    setWeek((prev) => {
      setLoadingWeek(prev?.id !== summaryId)
      return prev
    })
    api
      .getWeek(summaryId)
      .then((w) => {
        if (!cancelled) setWeek(keepIfSame(w))
      })
      .catch((err) => {
        if (!cancelled) report(err, 'Could not load that week.')
      })
      .finally(() => {
        if (!cancelled) setLoadingWeek(false)
      })
    return () => {
      cancelled = true
    }
  }, [summaryId, report])

  /* Poll so the two of us see each other's edits without a manual reload. */
  useEffect(() => {
    const sync = () => {
      if (document.hidden) return
      void refreshWeeks()
      void refreshBoot()
      const id = week?.id
      if (id) {
        api
          .getWeek(id)
          .then((fresh) => {
            // Never clobber a week the user has since navigated away from, and
            // leave the object alone entirely when nothing moved.
            setWeek((prev) =>
              prev && prev.id === fresh.id ? keepIfSame(fresh)(prev) : prev,
            )
          })
          .catch(() => {
            /* transient -- the next tick will retry */
          })
      }
    }
    const timer = setInterval(sync, POLL_MS)
    document.addEventListener('visibilitychange', sync)
    window.addEventListener('focus', sync)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', sync)
      window.removeEventListener('focus', sync)
    }
  }, [week?.id, refreshWeeks, refreshBoot])

  /** Apply a week returned by a mutation, and refresh the summary list. */
  const applyWeek = useCallback(
    (fresh: Week) => {
      setWeek(fresh)
      void refreshWeeks()
    },
    [refreshWeeks],
  )

  const onItemsChanged = useCallback(
    (items: Item[]) => setBoot((b) => (b ? { ...b, items } : b)),
    [],
  )

  const onMealsChanged = useCallback(
    (meals: Meal[]) => setBoot((b) => (b ? { ...b, meals } : b)),
    [],
  )

  const onPeopleChanged = useCallback(
    (people: Person[]) => setBoot((b) => (b ? { ...b, people } : b)),
    [],
  )

  const onReminderChanged = useCallback(
    (reminder: ReminderSettings) => setBoot((b) => (b ? { ...b, reminder } : b)),
    [],
  )

  const onAllergensChanged = useCallback(
    (allergens: ProfileAllergen[]) => setBoot((b) => (b ? { ...b, allergens } : b)),
    [],
  )

  async function signOut() {
    try {
      await authClient.signOut()
    } catch {
      /* the cookie is what matters, and it is gone either way */
    } finally {
      // Drop the offline copies too, so the next person at this device cannot
      // read a cached week straight off disk.
      pwa.clearCachedData()
      onSignedOut()
    }
  }


  if (declined) {
    return (
      <div className="lock">
        <div className="lock-card">
          <div className="lock-logo" aria-hidden="true">
            🥪
          </div>
          <h1>Not approved</h1>
          <p className="sub">{declined}</p>
          <button className="btn" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    )
  }

  if (fatal) {
    return (
      <div className="lock">
        <div className="lock-card">
          <div className="banner error">{fatal}</div>
        </div>
      </div>
    )
  }

  if (!boot) {
    return (
      <div className="lock">
        <p className="muted">Loading…</p>
      </div>
    )
  }

  return (
    <div
      className={`app ${phone ? 'has-tabbar' : ''} ${settingsDirty ? 'settings-dirty' : ''} ${
        pantryPicking ? 'pantry-picking' : ''
      }`}
    >
      <header className="topbar">
        <div className="brand">
          <span className="logo" aria-hidden="true">
            🥪
          </span>
          <span>Five Lunches</span>
          {/*
            Shown even for a family with one child, which it did not used to
            be. When this was only a switcher there was nothing to switch to
            and a lone caret said nothing; now it is also where a second child
            is added, and the family that has one is exactly the family that
            wants to add another. A menu of one name and "Add another child"
            is worth a caret.
          */}
          <Menu
            /* Names the child, so the button reads "Whose lunches: Ada"
               rather than leaving a screen reader to guess which of them
               is showing. */
            label={`Whose lunches: ${boot.profileName}`}
            className="who-switch"
            trigger={
              <>
                <span className="who">· {boot.profileName}</span>
                <span aria-hidden="true" className="who-caret">
                  ▾
                </span>
              </>
            }
            items={[
              ...children.map((k) => ({
                label: k.name,
                active: k.name === boot.profileName,
                onSelect: () => askThenSwitchChild(k.id),
              })),
              { kind: 'divider' as const },
              { label: 'Add another child…', icon: '＋', onSelect: () => setAddingChild(true) },
            ]}
          />
        </div>
        {pwa.canInstall && (
          <button className="btn sm" onClick={pwa.install}>
            Install
          </button>
        )}
        <span className="who">{session.name}</span>
        {/*
          * Labelled, not a bare cog. A 30px ghost glyph between "Evan" and
          * "Sign out" read as decoration, and Settings is where allergies,
          * children and notifications live -- too much to hide behind an
          * icon nobody is sure is a button. The word drops on a phone, where
          * the bar is tight and the ⋯ menu carries Settings as well.
          */}
        <button
          className="btn sm set-btn"
          onClick={() => setTab('settings')}
          aria-label="Settings"
          title="Children, contacts, allergies and notifications"
        >
          <span className="set-cog" aria-hidden="true">
            ⚙
          </span>
          <span className="set-label">Settings</span>
        </button>
        <button className="btn ghost sm" onClick={signOut}>
          Sign out
        </button>
      </header>

      {!phone && (
      <nav className="tabs" role="tablist" aria-label="Sections">
        {tabs.map((t) => (
          <button
            key={t.id}
            className="tab"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => changeTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>
      )}

      {pwa.offline && (
        <div className="pwa-bar offline" role="status">
          <span>
            <span aria-hidden="true">📡</span> Offline — showing the last version you loaded.
            Changes won&rsquo;t save until you reconnect.
          </span>
        </div>
      )}
      {pwa.updateReady && (
        <div className="pwa-bar update" role="status">
          <span>A new version is ready.</span>
          <button className="btn sm" onClick={pwa.applyUpdate}>
            Reload
          </button>
        </div>
      )}

      <div className="main">
        {tab === 'plan' && (
          <PlanView
            boot={boot}
            /* The other children, for "give this lunch to". */
            siblings={children.filter((k) => k.name !== boot.profileName)}
            weeks={weeks}
            weekStart={weekStart}
            week={week}
            loading={loadingWeek}
            onNavigate={setWeekStart}
            onWeek={applyWeek}
            onWeeksChanged={refreshWeeks}
            onMealsChanged={onMealsChanged}
            onItemsChanged={onItemsChanged}
            onManageMeals={() => setTab('meals')}
            onError={report}
          />
        )}
        {tab === 'pantry' && (
          <div className="content">
            <PantryPage
              boot={boot}
              onItemsChanged={onItemsChanged}
              onError={report}
              onPickingChange={setPantryPicking}
            />
          </div>
        )}
        {tab === 'meals' && (
          <div className="content">
            <MealsPage boot={boot} onMealsChanged={onMealsChanged} onError={report} />
          </div>
        )}
        {tab === 'assistant' && (
          <div className="content">
            <AssistantPage
              week={week}
              weekStart={weekStart}
              profileName={boot.profileName}
              pending={session.familyStatus !== 'active'}
              onWeekChanged={setWeek}
            />
          </div>
        )}
        {tab === 'history' && (
          <div className="content">
            <HistoryPage
              weeks={weeks}
              onOpen={(startDate) => {
                setWeekStart(startDate)
                setTab('plan')
              }}
              onChanged={refreshWeeks}
              onError={report}
            />
          </div>
        )}
        {tab === 'shop' && (
          <div className="content">
            <ShoppingPage
              boot={boot}
              week={week}
              weekStart={weekStart}
              onNavigate={setWeekStart}
              showInstall={nudge && phone}
              onInstall={() => setInstallOpen('plain')}
              onDismissInstall={dismissNudge}
              onError={report}
            />
          </div>
        )}
        {tab === 'settings' && (
          <SettingsPage
            boot={boot}
            theme={theme}
            onPeopleChanged={onPeopleChanged}
            onReminderChanged={onReminderChanged}
            onAllergensChanged={onAllergensChanged}
            onChildrenChanged={(list) => {
              setChildren(list)
              // A child removed from under the switcher must not stay selected.
              if (!list.some((k) => k.id === getActiveChild())) void switchChild(list[0]?.id ?? '')
            }}
            onFeedback={() => setFeedbackOpen(true)}
            onUnsavedChange={setSettingsDirty}
            onError={report}
          />
        )}
        {tab === 'admin' && session.isAdmin && (
          <div className="content">
            <AdminPage ownFamilyId={session.familyId ?? ''} categories={boot.categories} onError={report} />
          </div>
        )}
      </div>

      {installOpen && boot && (
        <InstallPrompt
          pwa={pwa}
          profileName={boot.profileName}
          reason={installOpen}
          onClose={() => {
            setInstallOpen(null)
            setNudge(false)
          }}
        />
      )}

      {/*
        Asked rather than assumed. Leaving is a legitimate thing to want -- the
        default is to stay, and the way out says what it costs.
      */}
      {leaving && (
        <Modal
          title="Leave without saving?"
          onClose={() => setLeaving(null)}
          footer={
            <>
              <button className="btn ghost" onClick={() => setLeaving(null)}>
                Stay
              </button>
              <div className="spacer" />
              <button
                className="btn danger"
                onClick={() => {
                  setSettingsDirty(false)
                  if ('tab' in leaving) setTab(leaving.tab)
                  else void switchChild(leaving.child)
                  setLeaving(null)
                }}
              >
                Leave without saving
              </button>
            </>
          }
        >
          <p style={{ margin: 0, fontSize: 14 }}>
            {boot.profileName}&rsquo;s allergies and restrictions have been changed and not saved.
            Leaving now loses the change.
          </p>
        </Modal>
      )}

      {addingChild && (
        <AddChild
          onClose={() => setAddingChild(false)}
          onAdded={(list, added) => {
            setChildren(list)
            setAddingChild(false)
            /* Straight to their board. You added a child because you are
               about to plan for them, and an empty week lands on the same
               starter prompt onboarding leaves you on. */
            if (added) void switchChild(added.id)
          }}
          onError={report}
        />
      )}

      {phone && (
        <TabBar
          tabs={BAR_TABS}
          active={tab}
          onSelect={(id) => changeTab(id as Tab)}
          moreActive={!BAR_TABS.some((t) => t.id === tab)}
          more={[
            ...(boot.aiAvailable
              ? [{ label: 'Ask the assistant', icon: '✨', active: tab === 'assistant', onSelect: () => changeTab('assistant') }]
              : []),
            { label: 'History', icon: '🕰', active: tab === 'history', onSelect: () => changeTab('history') },
            ...(session.isAdmin ? [{ label: 'Admin', icon: '🛡', active: tab === 'admin', onSelect: () => changeTab('admin') }] : []),
            { kind: 'divider' as const },
            { label: 'Settings', icon: '⚙', onSelect: () => setTab('settings') },
            { label: 'Send feedback', icon: '💬', onSelect: () => setFeedbackOpen(true) },
            ...(pwa.canInstall ? [{ label: 'Install the app', icon: '⬇', onSelect: pwa.install }] : []),
            { label: 'Sign out', icon: '↪', onSelect: () => void signOut() },
          ]}
        />
      )}
      <FeedbackTab onOpen={() => setFeedbackOpen(true)} />
      {feedbackOpen && (
        <FeedbackModal page={tab} weekStart={tab === 'plan' ? weekStart : ''} onClose={() => setFeedbackOpen(false)} />
      )}
    </div>
  )
}

/**
 * Add a child from the switcher.
 *
 * One field, because the moment this catches is "oh, and the little one" --
 * not a settings errand. Everything else about a child (rename, order,
 * remove, bring back) stays in Settings, where there is room for it.
 */
function AddChild({
  onClose,
  onAdded,
  onError,
}: {
  onClose: () => void
  onAdded: (children: Child[], added: Child | null) => void
  onError: (err: unknown, fallback: string) => void
}) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    const trimmed = name.trim()
    if (!trimmed || busy) return
    setBusy(true)
    try {
      // The route hands back the new child's id as well as the list, so the
      // switch that follows never has to match on a name.
      const { profiles, id } = await api.addChild(trimmed)
      onAdded(profiles, profiles.find((k) => k.id === id) ?? null)
    } catch (err) {
      onError(err, 'Could not add them.')
      setBusy(false)
    }
  }

  return (
    <Modal
      title="Add another child"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <div className="spacer" />
          <button className="btn primary" disabled={busy || !name.trim()} onClick={() => void save()}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="add-child">First name</label>
        <input
          id="add-child"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save()
          }}
          placeholder="Bo"
          maxLength={40}
          autoFocus
        />
        <span className="hint">
          They get their own board and their own allergies. The pantry stays the family&rsquo;s.
        </span>
      </div>
    </Modal>
  )
}
